import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import Button from "@material-ui/core/Button";
import ExpandMore from "@material-ui/icons/ExpandMore";
import Popover from "@material-ui/core/Popover";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import _ from "lodash";
import classNames from "classnames";

import useFilterConfig from "../../../../hooks/useFilterConfig";
import { useURLQueryString } from "../../../../hooks/useUrlQueryString";
import { useFilterButtonStyles } from "./filter-button.styles";
import { FilterButtonProps } from "./filter-button.props";

export const FilterButton = (props: FilterButtonProps) => {
  const classes = useFilterButtonStyles();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const { Form } = useFilterConfig(props.filterType);

  const {
    clearParams,
    getValues: getQueryValues,
    replaceHistory,
    setParams,
    values,
  } = useURLQueryString(props.controlledParams);

  const defaultValues = Form?.paramsToValues(values, props.optionLists);
  const valuesRef = useRef(defaultValues);

  const form = useForm({
    defaultValues: defaultValues ?? {},
    resolver: Form?.validation ?? undefined,
  });
  const { reset } = form;

  useEffect(() => {
    if (!_.isEqual(valuesRef.current, defaultValues)) {
      valuesRef.current = defaultValues;
      reset(defaultValues);
    }
  }, [defaultValues, reset]);

  if (!Form) {
    return null;
  }

  const handleApply = async () => {
    setParams(Form.valuesToParams(form.getValues()));
    !props.callback && replaceHistory();
    !!props.callback && props.callback(getQueryValues());
    handleClose();
  };

  const handleClear = () => {
    clearParams("all", !props.callback);
    !!props.callback && props.callback(getQueryValues());
    handleClose();
  };

  const handleClose = () => {
    setAnchorEl(null);
    reset();
  };

  const handleOpen = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const buttonClasses = classNames({
    [classes.button]: true,
    [classes.gridButton]: props.inGridView,
  });

  return (
    <form>
      <Button className={buttonClasses} endIcon={<ExpandMore />} onClick={handleOpen}>
        Filter
      </Button>
      <Popover
        aria-labelledby="form-dialog-title"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        keepMounted
        onClose={handleClose}
        open={open}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <DialogContent className={classes.popover}>
          <Form.Fields
            form={form}
            control={form.control}
            errors={form.formState.errors}
            optionLists={props.optionLists}
          />
        </DialogContent>
        <DialogActions className={classes.actions}>
          <Button onClick={handleClear} size="large">
            Clear all Filters
          </Button>
          <Button
            className={classes.apply}
            color="primary"
            onClick={form.handleSubmit(handleApply)}
            size="large"
            variant="contained"
          >
            Apply
          </Button>
        </DialogActions>
      </Popover>
    </form>
  );
};
